import type { ReactNode } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Alert,
  HStack,
  PrimaryButton,
  TertiaryButton,
  VStack,
} from '@enechain/ecloud-designsystem'

import { Modal } from '~/components/common/Modal'
import { UnsavedChangesDialog } from '~/components/common/UnsavedChangesDialog'
import { useUnsavedActionGuard } from '~/hooks/useUnsavedGuard'

type Props = {
  title: string
  errorMessage: string | undefined
  isPending: boolean
  isDirty: boolean
  /** 保存を押せない間 (必須の選択が済むまで)。 */
  isSaveDisabled?: boolean
  /** 閲覧のみ (保存ボタンを出さない)。 */
  isReadOnly?: boolean
  saveLabel?: string
  onClose: () => void
  onSave?: () => void
  /** Footer 左端 (削除)。 削除は Footer 左端 → 確認ダイアログの 2 ステップ。 */
  footerStart?: ReactNode
  /** キャンセルの手前に並べる操作 (見込みの「失注にする」「契約に切り替える」)。 */
  footerActions?: ReactNode
  children: ReactNode
}

/**
 * 顧客管理の登録・編集モーダルの枠 (モック `CustomerFormModal` と同じ配置)。
 * エラーは 1 件を上部の Alert に出す。 入力済みで閉じる時は破棄の確認を挟む。
 */
export const FormModalShell = ({
  title,
  errorMessage,
  isPending,
  isDirty,
  isSaveDisabled = false,
  isReadOnly = false,
  saveLabel,
  onClose,
  onSave,
  footerStart,
  footerActions,
  children,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const guard = useUnsavedActionGuard({ isDirty, beforeUnload: false })

  const footer = (
    <HStack justify="space-between" width="100%">
      {footerStart ?? <span />}
      <HStack gap={2}>
        {footerActions}
        <TertiaryButton onClick={() => guard.guardAction(onClose)}>
          {t('common:action.cancel')}
        </TertiaryButton>
        {!isReadOnly && (
          <PrimaryButton
            disabled={isSaveDisabled}
            loading={isPending}
            onClick={onSave}
          >
            {saveLabel ?? t('common:action.save')}
          </PrimaryButton>
        )}
      </HStack>
    </HStack>
  )

  return (
    <>
      <Modal
        footer={footer}
        isOpen
        onClose={() => guard.guardAction(onClose)}
        scrollBehavior="inside"
        size="xl"
        title={title}
      >
        <VStack align="stretch" gap={4}>
          {errorMessage !== undefined && (
            <Alert description={errorMessage} status="error" />
          )}
          {children}
        </VStack>
      </Modal>
      <UnsavedChangesDialog
        isOpen={guard.isBlocked}
        onCancel={guard.cancel}
        onConfirm={guard.proceed}
      />
    </>
  )
}
